import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import { 
  Radar, 
  TrendingUp,
  ExternalLink,
  ThumbsUp,
  Github,
  MessageSquare,
  HelpCircle,
  Newspaper,
  Loader2,
  ArrowRight,
  Sparkles
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const DiscoveryPage = () => {
  const { user } = useAuth();
  const [opportunities, setOpportunities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creatingRequest, setCreatingRequest] = useState(null);

  useEffect(() => {
    fetchOpportunities();
  }, []);

  const fetchOpportunities = async () => {
    try {
      const response = await axios.get(`${API}/discovery/opportunities`);
      setOpportunities(response.data);
    } catch (error) {
      console.error('Error fetching opportunities:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateRequest = async (opportunityId) => {
    if (!user) {
      toast.error('Please sign in to create a request');
      return;
    }
    
    setCreatingRequest(opportunityId);
    try {
      await axios.post(`${API}/discovery/opportunities/${opportunityId}/create-request`);
      toast.success('Integration request created!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create request');
    } finally {
      setCreatingRequest(null);
    }
  };

  const getSourceIcon = (source) => {
    switch (source) {
      case 'github':
        return <Github className="w-4 h-4" />;
      case 'reddit':
        return <MessageSquare className="w-4 h-4" />;
      case 'stackoverflow':
        return <HelpCircle className="w-4 h-4" />;
      case 'hackernews':
        return <Newspaper className="w-4 h-4" />;
      default:
        return <Radar className="w-4 h-4" />;
    }
  };

  const getSourceColor = (source) => {
    switch (source) {
      case 'github':
        return 'bg-slate-800 text-slate-300 border-slate-700';
      case 'reddit':
        return 'bg-orange-950/30 text-orange-400 border-orange-900/50';
      case 'stackoverflow':
        return 'bg-amber-950/30 text-amber-400 border-amber-900/50';
      case 'hackernews':
        return 'bg-orange-950/30 text-orange-500 border-orange-800/50';
      default:
        return 'bg-slate-800 text-slate-300 border-slate-700';
    }
  };

  return (
    <div className="pt-20 min-h-screen">
      <div className="max-w-7xl mx-auto px-6 lg:px-8 py-12">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-12">
          <div>
            <Badge className="mb-4 bg-cyan-950/30 text-cyan-400 border-cyan-900/50">
              <Radar className="w-3.5 h-3.5 mr-1.5" />
              Discovery Engine
            </Badge>
            <h1 className="font-heading text-4xl md:text-5xl font-bold tracking-tight text-white mb-4">
              Integration Opportunities
            </h1>
            <p className="text-lg text-slate-400">
              AI-discovered integration opportunities from GitHub, Reddit, StackOverflow, and more
            </p>
          </div>
          <Link to="/generator">
            <Button className="glow-primary bg-primary hover:bg-primary/90">
              <Sparkles className="w-5 h-5 mr-2" />
              Generate Connector
            </Button>
          </Link>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-12">
          <div className="bg-card border border-white/5 rounded-xl p-6">
            <Radar className="w-8 h-8 text-cyan-400 mb-2" />
            <div className="text-3xl font-heading font-bold text-white">{opportunities.length}</div>
            <p className="text-sm text-slate-500">Discovered</p>
          </div>
          <div className="bg-card border border-white/5 rounded-xl p-6">
            <Github className="w-8 h-8 text-slate-400 mb-2" />
            <div className="text-3xl font-heading font-bold text-white">
              {opportunities.filter(o => o.source === 'github').length}
            </div>
            <p className="text-sm text-slate-500">From GitHub</p>
          </div>
          <div className="bg-card border border-white/5 rounded-xl p-6">
            <MessageSquare className="w-8 h-8 text-orange-400 mb-2" />
            <div className="text-3xl font-heading font-bold text-white">
              {opportunities.filter(o => o.source === 'reddit').length}
            </div>
            <p className="text-sm text-slate-500">From Reddit</p>
          </div>
          <div className="bg-card border border-white/5 rounded-xl p-6">
            <TrendingUp className="w-8 h-8 text-emerald-400 mb-2" />
            <div className="text-3xl font-heading font-bold text-white">
              {opportunities.reduce((sum, o) => sum + o.mentions_count, 0)}
            </div>
            <p className="text-sm text-slate-500">Total Mentions</p>
          </div>
        </div>

        {/* Opportunities List */}
        {loading ? (
          <div className="flex items-center justify-center py-24">
            <Loader2 className="w-8 h-8 text-primary animate-spin" />
          </div>
        ) : (
          <div className="space-y-4">
            {opportunities.map((opportunity, index) => (
              <div 
                key={opportunity.id}
                className="bg-card border border-white/5 rounded-xl p-6 hover:border-white/10 transition-colors"
                data-testid={`opportunity-${opportunity.id}`}
              >
                <div className="flex items-start gap-4">
                  {/* Demand Score */}
                  <div className="flex flex-col items-center gap-1 p-3 rounded-lg bg-gradient-to-b from-cyan-500/20 to-cyan-500/5 border border-cyan-500/30">
                    <TrendingUp className="w-5 h-5 text-cyan-400" />
                    <span className="font-bold text-xl text-cyan-400">{opportunity.demand_score}</span>
                    <span className="text-xs text-cyan-500">score</span>
                  </div>

                  {/* Content */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-3 mb-2">
                      <h3 className="font-heading text-xl font-semibold text-white">
                        {opportunity.service_name}
                      </h3>
                      <Badge className={`${getSourceColor(opportunity.source)} capitalize flex items-center gap-1`}>
                        {getSourceIcon(opportunity.source)}
                        {opportunity.source}
                      </Badge>
                      {opportunity.api_available && (
                        <Badge className="bg-emerald-950/30 text-emerald-400 border-emerald-900/50">
                          API Available
                        </Badge>
                      )}
                      {index < 3 && (
                        <Badge className="bg-amber-950/30 text-amber-400 border-amber-900/50">
                          Hot
                        </Badge>
                      )}
                    </div>
                    
                    {opportunity.description && (
                      <p className="text-slate-400 mb-3">{opportunity.description}</p>
                    )}
                    
                    <div className="flex items-center gap-4 text-sm text-slate-500">
                      <span>{opportunity.mentions_count} mentions</span>
                      {opportunity.api_docs_url && (
                        <a 
                          href={opportunity.api_docs_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="flex items-center gap-1 text-primary hover:underline"
                        >
                          <ExternalLink className="w-4 h-4" />
                          API Docs
                        </a>
                      )}
                      <a 
                        href={opportunity.source_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center gap-1 hover:text-white transition-colors"
                      >
                        <ExternalLink className="w-4 h-4" />
                        View Source
                      </a>
                    </div>
                  </div>

                  {/* Actions */}
                  <div className="flex flex-col gap-2">
                    <Button
                      onClick={() => handleCreateRequest(opportunity.id)}
                      disabled={creatingRequest === opportunity.id}
                      variant="outline"
                      className="border-slate-700 text-slate-300 hover:bg-secondary"
                    >
                      {creatingRequest === opportunity.id ? (
                        <Loader2 className="w-4 h-4 animate-spin" />
                      ) : (
                        <>
                          <ThumbsUp className="w-4 h-4 mr-2" />
                          Request
                        </>
                      )}
                    </Button>
                    <Link to={`/generator?service=${encodeURIComponent(opportunity.service_name)}&docs=${encodeURIComponent(opportunity.api_docs_url || '')}`}>
                      <Button className="w-full bg-primary hover:bg-primary/90">
                        <Sparkles className="w-4 h-4 mr-2" />
                        Generate
                      </Button>
                    </Link>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default DiscoveryPage;
